import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import {
  Spinner,
  Row,
  Col,
  Table,
  Card,
  CardBody,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { getAgentSessions, updateAgentSession } from "../ApiCalls/mantisAgents";
import * as XLSX from "xlsx";
import "./styles/sessions.scss";

const select = (state) => {
  return {
    userId: state.auth.userId,
    token: state.auth.token,
  };
};

const PAGE_SIZE = 20;

/**
 * Event/session APIs often send Unix time in seconds; `new Date(sec)` treats the
 * value as ms and shows dates near 1970. Also supports protobuf-style { seconds, nanos }.
 */
function coerceToDate(input) {
  if (input == null || input === "") return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === "object") {
    if (typeof input.seconds === "number") {
      const ms =
        input.seconds * 1000 +
        (typeof input.nanos === "number" ? input.nanos / 1e6 : 0);
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof input.seconds === "string" && input.seconds !== "") {
      const s = Number(input.seconds);
      const nsRaw = input.nanos;
      const ns =
        typeof nsRaw === "number"
          ? nsRaw
          : nsRaw != null && nsRaw !== ""
          ? Number(nsRaw)
          : 0;
      if (!Number.isFinite(s)) return null;
      const d = new Date(s * 1000 + (Number.isFinite(ns) ? ns / 1e6 : 0));
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  if (typeof input === "number" && Number.isFinite(input)) {
    if (Math.abs(input) < 1e12) {
      const d = new Date(input * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === "string") {
    const t = input.trim();
    if (/^-?\d+(\.\d+)?$/.test(t)) {
      const n = Number(t);
      if (!Number.isFinite(n)) return null;
      if (Math.abs(n) < 1e12) {
        const d = new Date(n * 1000);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(n);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

const Sessions = (props) => {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [eventsModal, setEventsModal] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [collapsedEvents, setCollapsedEvents] = useState(new Set());
  const [eventsLoadingSessionId, setEventsLoadingSessionId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    empresa: "",
    estado: "",
    agente: "",
  });
  const [debouncedEmpresa, setDebouncedEmpresa] = useState("");
  const [debouncedAgente, setDebouncedAgente] = useState("");
  const empresaDebounceBoot = useRef(false);
  const agenteDebounceBoot = useRef(false);

  useEffect(() => {
    if (!empresaDebounceBoot.current) {
      empresaDebounceBoot.current = true;
      setDebouncedEmpresa(filters.empresa.trim());
      return;
    }
    const id = setTimeout(() => {
      setDebouncedEmpresa(filters.empresa.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(id);
  }, [filters.empresa]);

  useEffect(() => {
    if (!agenteDebounceBoot.current) {
      agenteDebounceBoot.current = true;
      setDebouncedAgente(filters.agente.trim());
      return;
    }
    const id = setTimeout(() => {
      setDebouncedAgente(filters.agente.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(id);
  }, [filters.agente]);

  const loadSessions = useCallback(async () => {
    if (!props.token) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getAgentSessions(props.token, {
        page,
        pageSize: PAGE_SIZE,
        businessName: debouncedEmpresa || undefined,
        agentName: debouncedAgente || undefined,
        status: filters.estado || undefined,
      });
      if (result.success) {
        setSessions(result.data || []);
        setTotal(typeof result.total === "number" ? result.total : 0);
      } else {
        setError("Error al cargar las sesiones");
      }
    } catch (err) {
      setError("Error al cargar las sesiones");
      console.error("Error fetching sessions:", err);
    } finally {
      setLoading(false);
    }
  }, [
    props.token,
    page,
    debouncedEmpresa,
    debouncedAgente,
    filters.estado,
  ]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const fetchSessions = () => loadSessions();

  // The list endpoint omits the (potentially large) events array by default.
  // Bulk operations that actually need it (event modal, export) request the
  // current page again with includeEvents:true instead of loading it eagerly
  // on every paginated fetch.
  const fetchCurrentPageWithEvents = async () => {
    const result = await getAgentSessions(props.token, {
      page,
      pageSize: PAGE_SIZE,
      businessName: debouncedEmpresa || undefined,
      agentName: debouncedAgente || undefined,
      status: filters.estado || undefined,
      includeEvents: true,
    });
    return result.data || [];
  };

  const openViewModal = (session) => {
    setSelectedSession(session);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setSelectedSession(null);
  };

  const showEventsModalFor = (events) => {
    setSelectedEvents(events || []);
    // Set all events as collapsed by default, except the first one
    const eventsToCollapse = new Set();
    if (events && events.length > 1) {
      for (let i = 1; i < events.length; i++) {
        eventsToCollapse.add(i);
      }
    }
    setCollapsedEvents(eventsToCollapse);
    setEventsModal(true);
  };

  const openEventsModal = async (session) => {
    if (!session) return;
    if (Array.isArray(session.events)) {
      showEventsModalFor(session.events);
      return;
    }
    if (!session.eventsCount) {
      showEventsModalFor([]);
      return;
    }
    try {
      setEventsLoadingSessionId(session._id);
      const pageWithEvents = await fetchCurrentPageWithEvents();
      const full = pageWithEvents.find((s) => s._id === session._id);
      showEventsModalFor(full?.events || []);
    } catch (err) {
      console.error("Error fetching session events:", err);
      setError("Error al cargar los eventos de la sesión");
    } finally {
      setEventsLoadingSessionId(null);
    }
  };

  const closeEventsModal = () => {
    setEventsModal(false);
    setSelectedEvents([]);
    setCollapsedEvents(new Set());
  };

  const toggleEventCollapse = (eventIndex) => {
    setCollapsedEvents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(eventIndex)) {
        newSet.delete(eventIndex);
      } else {
        newSet.add(eventIndex);
      }
      return newSet;
    });
  };

  const clearFilters = () => {
    setFilters({
      empresa: "",
      estado: "",
      agente: "",
    });
    setPage(1);
  };

  // The list response carries eventsCount instead of the full events array;
  // fall back to events.length for responses that did include it (e.g. export).
  const getSessionEventsCount = (session) =>
    typeof session.eventsCount === "number"
      ? session.eventsCount
      : session.events
      ? session.events.length
      : 0;

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (field === "estado") {
      setPage(1);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (total >= 0 && page > tp) {
      setPage(tp);
    }
  }, [total, page]);

  // Helper function to extract conversational parts from events
  const extractConversationalParts = (event) => {
    const parts = event?.content?.parts || [];
    const conversationalParts = [];

    for (const part of parts) {
      if (part.text) {
        conversationalParts.push({
          text: part.text,
          role: event.content?.role || "assistant",
        });
      }
    }

    return conversationalParts;
  };

  // Helper function to get last 10 conversation messages from a session
  const getLastConversationMessages = (session) => {
    if (!session.events || session.events.length === 0) {
      return "Sin conversaciones";
    }

    const allMessages = [];

    // Extract all conversational parts from all events
    session.events.forEach((event) => {
      const conversationalParts = extractConversationalParts(event);
      conversationalParts.forEach((part) => {
        allMessages.push({
          text: part.text,
          role: part.role,
          timestamp: event.timestamp,
        });
      });
    });

    // Sort by timestamp (most recent first)
    allMessages.sort(
      (a, b) =>
        (coerceToDate(b.timestamp)?.getTime() || 0) -
        (coerceToDate(a.timestamp)?.getTime() || 0)
    );

    // Get last 10 messages
    const lastMessages = allMessages.slice(0, 10);

    if (lastMessages.length === 0) {
      return "Sin mensajes de texto";
    }

    // Format messages as a readable conversation
    return lastMessages
      .map((msg, index) => {
        const roleLabel = msg.role === "user" ? "Usuario" : "Asistente";
        return `${index + 1}. [${roleLabel}] ${msg.text}`;
      })
      .join("\n\n");
  };

  const exportToExcel = async () => {
    if (sessions.length === 0) {
      alert("No hay sesiones para exportar en la página actual");
      return;
    }

    setExportLoading(true);
    try {
      // The loaded page omits events by default; re-fetch it with events
      // included so the export sheets have full conversational data.
      const sessions = await fetchCurrentPageWithEvents();

    // Create workbook
    const wb = XLSX.utils.book_new();

    // 1. Main Sessions Worksheet
    const sessionsData = sessions.map((session) => ({
      "ID de Sesión": session.sessionId,
      Agente: session.mantisAgent?.name || "N/A",
      Empresa: session.mantisBusiness?.name || "Sin empresa",
      Email: session.email || "N/A",
      Teléfono: session.phoneNumber || "N/A",
      Estado: session.status === "OPEN" ? "Abierta" : "Cerrada",
      Control: session.agentControlled
        ? "Controlado por IA"
        : "Controlado por Usuario",
      "ID del Agente": session.agentId,
      "ID de Negocio": session.mantisBusinessId || "N/A",
      "ID de Usuario": session.mantisBusinessUserId || "N/A",
      "Fecha de Creación": session.dateCreated
        ? formatDate(session.dateCreated)
        : "N/A",
      "Última Actualización": session.dateUpdated
        ? formatDate(session.dateUpdated)
        : "N/A",
      "Fecha de Cierre": session.dateClosed
        ? formatDate(session.dateClosed)
        : "Sesión abierta",
      "Número de Eventos": session.events ? session.events.length : 0,
      "Últimas 10 Conversaciones": getLastConversationMessages(session),
    }));

    const sessionsWs = XLSX.utils.json_to_sheet(sessionsData);

    // Set column widths for sessions
    const sessionsColWidths = [
      { wch: 20 }, // ID de Sesión
      { wch: 25 }, // Agente
      { wch: 25 }, // Empresa
      { wch: 30 }, // Email
      { wch: 15 }, // Teléfono
      { wch: 12 }, // Estado
      { wch: 20 }, // Control
      { wch: 20 }, // ID del Agente
      { wch: 20 }, // ID de Negocio
      { wch: 20 }, // ID de Usuario
      { wch: 20 }, // Fecha de Creación
      { wch: 20 }, // Última Actualización
      { wch: 20 }, // Fecha de Cierre
      { wch: 15 }, // Número de Eventos
      { wch: 80 }, // Últimas 10 Conversaciones
    ];
    sessionsWs["!cols"] = sessionsColWidths;
    XLSX.utils.book_append_sheet(wb, sessionsWs, "Sesiones");

    // 2. Conversational Events Worksheet (filtered to only conversational parts)
    const conversationalEventsData = [];
    sessions.forEach((session) => {
      if (session.events && session.events.length > 0) {
        session.events.forEach((event, eventIndex) => {
          const conversationalParts = extractConversationalParts(event);

          conversationalParts.forEach((part, partIndex) => {
            conversationalEventsData.push({
              "ID de Sesión": session.sessionId,
              Agente: session.mantisAgent?.name || "N/A",
              Empresa: session.mantisBusiness?.name || "Sin empresa",
              Timestamp: event.timestamp ? formatDate(event.timestamp) : "N/A",
              "Contenido Conversacional": part.text,
              Rol: part.role,
              "Longitud del Texto": part.text.length,
            });
          });
        });
      }
    });

    if (conversationalEventsData.length > 0) {
      const eventsWs = XLSX.utils.json_to_sheet(conversationalEventsData);

      // Set column widths for conversational events
      const eventsColWidths = [
        { wch: 20 },
        { wch: 25 },
        { wch: 25 },
        { wch: 20 },
        { wch: 60 },
        { wch: 15 },
        { wch: 15 },
      ];
      eventsWs["!cols"] = eventsColWidths;
      XLSX.utils.book_append_sheet(wb, eventsWs, "Conversaciones");
    }

    // 3. Summary Worksheet
    const totalConversationalParts = sessions.reduce((sum, session) => {
      if (session.events) {
        return (
          sum +
          session.events.reduce((eventSum, event) => {
            return eventSum + extractConversationalParts(event).length;
          }, 0)
        );
      }
      return sum;
    }, 0);

    const summaryData = [
      {
        Métrica: "Alcance del export",
        Valor: `Solo página ${page} (${sessions.length} filas; total en servidor con filtros: ${total})`,
      },
      { Métrica: "Filas en esta página", Valor: sessions.length },
      {
        Métrica: "Sesiones Abiertas (página)",
        Valor: sessions.filter((s) => s.status === "OPEN").length,
      },
      {
        Métrica: "Sesiones Cerradas (página)",
        Valor: sessions.filter((s) => s.status === "CLOSED").length,
      },
      {
        Métrica: "Total de Eventos (página)",
        Valor: sessions.reduce(
          (sum, s) => sum + (s.events ? s.events.length : 0),
          0
        ),
      },
      {
        Métrica: "Total de Partes Conversacionales (página)",
        Valor: totalConversationalParts,
      },
      {
        Métrica: "Sesiones con Eventos (página)",
        Valor: sessions.filter((s) => s.events && s.events.length > 0).length,
      },
      {
        Métrica: "Sesiones sin Eventos (página)",
        Valor: sessions.filter((s) => !s.events || s.events.length === 0).length,
      },
      {
        Métrica: "Controladas por IA (página)",
        Valor: sessions.filter((s) => s.agentControlled).length,
      },
      {
        Métrica: "Controladas por Usuario (página)",
        Valor: sessions.filter((s) => !s.agentControlled).length,
      },
    ];

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    const summaryColWidths = [{ wch: 25 }, { wch: 15 }];
    summaryWs["!cols"] = summaryColWidths;
    XLSX.utils.book_append_sheet(wb, summaryWs, "Resumen");

    // Generate filename with current date
    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `sesiones_agentes_${currentDate}.xlsx`;

    // Export file
    XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error("Error exporting sessions:", err);
      alert("Error al exportar las sesiones. Por favor, intenta de nuevo.");
    } finally {
      setExportLoading(false);
    }
  };

  const closeSession = async (sessionId) => {
    try {
      const result = await updateAgentSession(props.token, sessionId, {
        status: "CLOSED",
        dateClosed: new Date(),
      });

      if (result.success) {
        // Update the local sessions state
        setSessions((prevSessions) =>
          prevSessions.map((session) =>
            session._id === sessionId
              ? { ...session, status: "CLOSED", dateClosed: new Date() }
              : session
          )
        );

        // Update the selected session if it's the same one
        if (selectedSession && selectedSession._id === sessionId) {
          setSelectedSession((prev) => ({
            ...prev,
            status: "CLOSED",
            dateClosed: new Date(),
          }));
        }

        // Close the modal
        closeViewModal();
      } else {
        console.error("Error closing session:", result.error);
        alert("Error al cerrar la sesión");
      }
    } catch (error) {
      console.error("Error closing session:", error);
      alert("Error al cerrar la sesión");
    }
  };

  const formatDate = (dateInput) => {
    const d = coerceToDate(dateInput);
    if (!d) return "N/A";
    return d.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      OPEN: {
        color: "light",
        text: "Abierta",
        textColor: "#212529",
        borderColor: "success",
      },
      CLOSED: {
        color: "light",
        text: "Cerrada",
        textColor: "#212529",
        borderColor: "secondary",
      },
    };

    const config = statusConfig[status] || {
      color: "light",
      text: status,
      textColor: "#212529",
      borderColor: "info",
    };

    return (
      <Badge
        color={config.color}
        className="status-badge"
        style={{
          color: config.textColor,
          border: `1px solid var(--bs-${config.borderColor})`,
        }}
      >
        {config.text}
      </Badge>
    );
  };

  const getAgentControlledBadge = (controlled) => {
    return (
      <Badge
        color="light"
        className="me-2"
        style={{
          color: "#212529",
          border: `1px solid var(--bs-${controlled ? "primary" : "warning"})`,
        }}
      >
        {controlled ? "Controlado por IA" : "Controlado por Usuario"}
      </Badge>
    );
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "N/A";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const renderJsonValue = (value, level = 0) => {
    // Prevent infinite recursion
    if (level > 10) {
      return <span className="text-warning">[Max depth reached]</span>;
    }

    if (value === null) return <span className="text-muted">null</span>;
    if (value === undefined)
      return <span className="text-muted">undefined</span>;

    if (typeof value === "string") {
      return <span className="text-success">"{value}"</span>;
    }

    if (typeof value === "number") {
      return <span className="text-primary">{value}</span>;
    }

    if (typeof value === "boolean") {
      return <span className="text-warning">{value.toString()}</span>;
    }

    if (typeof value === "function") {
      return <span className="text-info">[Function]</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-muted">[]</span>;

      return (
        <div className="json-array">
          <span className="text-muted">[</span>
          {value.map((item, index) => (
            <div
              key={index}
              className="json-item"
              style={{ marginLeft: "20px" }}
            >
              {renderJsonValue(item, level + 1)}
              {index < value.length - 1 && (
                <span className="text-muted">,</span>
              )}
            </div>
          ))}
          <span className="text-muted">]</span>
        </div>
      );
    }

    if (typeof value === "object" && value !== null) {
      // Check if it's a DOM element or React component
      if (value.nodeType || value.$$typeof) {
        return <span className="text-info">[React Element]</span>;
      }

      const keys = Object.keys(value);
      if (keys.length === 0) return <span className="text-muted">{}</span>;

      return (
        <div className="json-object">
          <span className="text-muted">{"{"}</span>
          {keys.map((key, index) => (
            <div key={key} className="json-item" style={{ marginLeft: "20px" }}>
              <span className="text-info">"{key}"</span>
              <span className="text-muted">: </span>
              {renderJsonValue(value[key], level + 1)}
              {index < keys.length - 1 && <span className="text-muted">,</span>}
            </div>
          ))}
          <span className="text-muted">{"}"}</span>
        </div>
      );
    }

    // Handle any other types that might cause issues
    try {
      const stringValue = String(value);
      // Check if the string representation is too long
      if (stringValue.length > 100) {
        return (
          <span className="text-muted">
            "{stringValue.substring(0, 100)}..."
          </span>
        );
      }
      return <span>{stringValue}</span>;
    } catch (error) {
      return <span className="text-danger">[Complex Object]</span>;
    }
  };

  return (
    <div className="sessions-container">
      {loading ? (
        <div className="loading-container text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3">Cargando sesiones...</p>
        </div>
      ) : error ? (
        <div className="error-container text-center py-5">
          <p className="text-danger">{error}</p>
          <Button color="primary" onClick={fetchSessions}>
            Reintentar
          </Button>
        </div>
      ) : (
        <Row>
          <Col xs={12}>
            <Card className="sessions-card">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Sesiones de Agentes</h5>
                  <div className="d-flex gap-2">
                    <Button color="primary" size="sm" onClick={fetchSessions}>
                      <i className="fas fa-sync-alt me-1"></i>
                      Actualizar
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      onClick={exportToExcel}
                      disabled={sessions.length === 0 || exportLoading}
                      title={
                        sessions.length === 0
                          ? "No hay sesiones en la página actual"
                          : "Exportar la página actual a Excel (incluye métricas solo de esta página)"
                      }
                    >
                      {exportLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-file-excel me-2"></i>
                          Exportar Excel
                        </>
                      )}
                    </Button>
                    <Button
                      color="outline-secondary"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <i className="fas fa-filter me-2"></i>
                      {showFilters ? "Ocultar Filtros" : "Filtros"}
                    </Button>
                  </div>
                </div>

                {/* Filters Section */}
                {showFilters && (
                  <div className="filters-section mb-4 p-3 bg-light rounded border">
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label fw-bold text-muted small">
                          <i className="fas fa-building me-1"></i>
                          Empresa
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Filtrar por empresa..."
                          value={filters.empresa}
                          onChange={(e) =>
                            handleFilterChange("empresa", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-bold text-muted small">
                          <i className="fas fa-robot me-1"></i>
                          Agente
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Filtrar por agente..."
                          value={filters.agente}
                          onChange={(e) =>
                            handleFilterChange("agente", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-bold text-muted small">
                          <i className="fas fa-circle me-1"></i>
                          Estado
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={filters.estado}
                          onChange={(e) =>
                            handleFilterChange("estado", e.target.value)
                          }
                        >
                          <option value="">Todos los estados</option>
                          <option value="OPEN">Abierta</option>
                          <option value="CLOSED">Cerrada</option>
                        </select>
                      </div>
                      <div className="col-md-3 d-flex align-items-end">
                        <div className="d-flex gap-2 w-100">
                          <Button
                            color="outline-secondary"
                            size="sm"
                            onClick={clearFilters}
                            className="flex-fill"
                          >
                            <i className="fas fa-times me-1"></i>
                            Limpiar
                          </Button>
                          <Button
                            color="secondary"
                            size="sm"
                            onClick={() => setShowFilters(false)}
                            className="flex-fill"
                          >
                            <i className="fas fa-eye-slash me-1"></i>
                            Ocultar
                          </Button>
                        </div>
                      </div>
                    </div>
                    {Object.values(filters).some((filter) => filter !== "") && (
                      <div className="mt-3 pt-3 border-top">
                        <div className="d-flex align-items-center gap-2">
                          <small className="text-muted fw-bold">
                            <i className="fas fa-filter me-1"></i>
                            Filtros activos:
                          </small>
                          {filters.empresa && (
                            <Badge color="info" className="small">
                              Empresa: {filters.empresa}
                            </Badge>
                          )}
                          {filters.agente && (
                            <Badge color="info" className="small">
                              Agente: {filters.agente}
                            </Badge>
                          )}
                          {filters.estado && (
                            <Badge color="info" className="small">
                              Estado:{" "}
                              {filters.estado === "OPEN"
                                ? "Abierta"
                                : "Cerrada"}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {sessions.length > 0 ? (
                  <Table responsive className="sessions-table">
                    <thead>
                      <tr>
                        <th>ID de Sesión</th>
                        <th>Agente</th>
                        <th>Empresa</th>
                        <th>Usuario</th>
                        <th>Estado</th>
                        <th>Control</th>
                        <th>Eventos</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((session) => (
                        <tr key={session._id}>
                          <td className="session-id">
                            <strong className="font-monospace">
                              {session.sessionId}
                            </strong>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border border-secondary">
                              {truncateText(
                                session.mantisAgent?.name ||
                                  "Agente no encontrado",
                                20
                              )}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border border-secondary">
                              {truncateText(
                                session.mantisBusiness?.name || "Sin empresa",
                                20
                              )}
                            </span>
                          </td>
                          <td>
                            <div>
                              <div className="fw-bold">
                                {truncateText(session.email, 25)}
                              </div>
                              {session.phoneNumber && (
                                <small className="text-muted">
                                  {session.phoneNumber}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>{getStatusBadge(session.status)}</td>
                          <td>
                            {getAgentControlledBadge(session.agentControlled)}
                          </td>
                          <td className="events-cell">
                            <Button
                              size="sm"
                              onClick={() => openEventsModal(session)}
                              title="Ver eventos"
                              disabled={
                                !getSessionEventsCount(session) ||
                                eventsLoadingSessionId === session._id
                              }
                              style={{
                                borderColor: "#d2691e",
                                color: "#d2691e",
                                backgroundColor: "transparent",
                              }}
                              className="btn-outline-custom"
                            >
                              {eventsLoadingSessionId === session._id ? (
                                <Spinner size="sm" />
                              ) : (
                                <>
                                  <i className="fas fa-list"></i>
                                  <span className="ms-1">
                                    {getSessionEventsCount(session)}
                                  </span>
                                </>
                              )}
                            </Button>
                          </td>
                          <td className="actions-cell">
                            <Button
                              color="outline-primary"
                              size="sm"
                              onClick={() => openViewModal(session)}
                              title="Ver detalles"
                            >
                              <i className="fas fa-eye"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="fas fa-comments fa-3x mb-3"></i>
                    <p>
                      {total === 0
                        ? "No hay sesiones que coincidan con los filtros (o aún no hay sesiones)"
                        : "No hay resultados en esta página"}
                    </p>
                    <p className="small">
                      {total === 0
                        ? "Las sesiones de agentes aparecerán aquí cuando se creen, o prueba otros filtros"
                        : "Usa la paginación o cambia de página"}
                    </p>
                  </div>
                )}
                {total > 0 && (
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                    <small className="text-muted">
                      Mostrando {(page - 1) * PAGE_SIZE + 1}–
                      {Math.min(page * PAGE_SIZE, total)} de {total} sesiones
                    </small>
                    <Pagination className="mb-0">
                      <PaginationItem disabled={page <= 1}>
                        <PaginationLink
                          previous
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        />
                      </PaginationItem>
                      <PaginationItem disabled>
                        <PaginationLink>
                          {page} / {totalPages}
                        </PaginationLink>
                      </PaginationItem>
                      <PaginationItem disabled={page >= totalPages}>
                        <PaginationLink
                          next
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                        />
                      </PaginationItem>
                    </Pagination>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Session Details Modal */}
      <Modal isOpen={viewModal} toggle={closeViewModal} size="xl">
        <ModalHeader toggle={closeViewModal} className="bg-primary text-white">
          <i className="fas fa-comments me-2"></i>
          Detalles de la Sesión: {selectedSession?.sessionId}
        </ModalHeader>
        <ModalBody className="p-4">
          {selectedSession && (
            <div className="session-details">
              {/* Header Section */}
              <div className="text-center mb-4">
                <div className="session-avatar mb-3">
                  <i className="fas fa-comments fa-4x text-primary"></i>
                </div>
                <h3 className="text-primary mb-2">
                  Sesión {selectedSession.sessionId}
                </h3>
                <div className="d-flex justify-content-center gap-2">
                  {getStatusBadge(selectedSession.status)}
                  {getAgentControlledBadge(selectedSession.agentControlled)}
                </div>
              </div>

              <Row>
                {/* Basic Information */}
                <Col lg={6} className="mb-4">
                  <Card className="h-100 border-0 shadow-sm">
                    <CardBody>
                      <h5 className="card-title text-primary mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Información de la Sesión
                      </h5>

                      <div className="info-item mb-3">
                        <label className="fw-bold text-muted">
                          ID de Sesión:
                        </label>
                        <p className="mb-1 font-monospace">
                          {selectedSession.sessionId}
                        </p>
                      </div>

                      <div className="info-item mb-3">
                        <label className="fw-bold text-muted">
                          ID del Agente:
                        </label>
                        <p className="mb-1 font-monospace">
                          {selectedSession.agentId}
                        </p>
                      </div>

                      <div className="info-item mb-3">
                        <label className="fw-bold text-muted">Estado:</label>
                        <div className="mt-1">
                          {getStatusBadge(selectedSession.status)}
                        </div>
                      </div>

                      <div className="info-item mb-3">
                        <label className="fw-bold text-muted">Control:</label>
                        <div className="mt-1">
                          {getAgentControlledBadge(
                            selectedSession.agentControlled
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* User Information */}
                <Col lg={6} className="mb-4">
                  <Card className="h-100 border-0 shadow-sm">
                    <CardBody>
                      <h5 className="card-title text-primary mb-3">
                        <i className="fas fa-user me-2"></i>
                        Información del Usuario
                      </h5>

                      <div className="info-item mb-3">
                        <label className="fw-bold text-muted">Email:</label>
                        <p className="mb-1">
                          {selectedSession.email || "No especificado"}
                        </p>
                      </div>

                      <div className="info-item mb-3">
                        <label className="fw-bold text-muted">Teléfono:</label>
                        <p className="mb-1">
                          {selectedSession.phoneNumber || "No especificado"}
                        </p>
                      </div>

                      <div className="info-item mb-3">
                        <label className="fw-bold text-muted">
                          ID de Negocio:
                        </label>
                        <p className="mb-1 font-monospace">
                          {selectedSession.mantisBusinessId ||
                            "No especificado"}
                        </p>
                      </div>

                      <div className="info-item mb-3">
                        <label className="fw-bold text-muted">
                          ID de Usuario:
                        </label>
                        <p className="mb-1 font-monospace">
                          {selectedSession.mantisBusinessUserId ||
                            "No especificado"}
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Context Information */}
              {selectedSession.context &&
                Object.keys(selectedSession.context).length > 0 && (
                  <Row>
                    <Col xs={12} className="mb-4">
                      <Card className="border-0 shadow-sm">
                        <CardBody>
                          <h5 className="card-title text-primary mb-3">
                            <i className="fas fa-cogs me-2"></i>
                            Contexto de la Sesión
                          </h5>
                          <div className="info-item">
                            <pre className="mb-0 bg-light p-3 rounded">
                              {JSON.stringify(selectedSession.context, null, 2)}
                            </pre>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}

              {/* Timestamps */}
              <Row>
                <Col xs={12}>
                  <Card className="border-0 shadow-sm">
                    <CardBody>
                      <h5 className="card-title text-primary mb-3">
                        <i className="fas fa-clock me-2"></i>
                        Fechas y Horarios
                      </h5>
                      <Row>
                        <Col md={4}>
                          <div className="info-item mb-3">
                            <label className="fw-bold text-muted">
                              Fecha de Creación:
                            </label>
                            <p className="mb-1">
                              {formatDate(selectedSession.dateCreated)}
                            </p>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="info-item mb-3">
                            <label className="fw-bold text-muted">
                              Última Actualización:
                            </label>
                            <p className="mb-1">
                              {formatDate(selectedSession.dateUpdated)}
                            </p>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="info-item mb-3">
                            <label className="fw-bold text-muted">
                              Fecha de Cierre:
                            </label>
                            <p className="mb-1">
                              {selectedSession.dateClosed
                                ? formatDate(selectedSession.dateClosed)
                                : "Sesión abierta"}
                            </p>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button color="secondary" onClick={closeViewModal}>
            <i className="fas fa-times me-2"></i>
            Cerrar
          </Button>
          {selectedSession?.status === "OPEN" && (
            <Button
              color="warning"
              onClick={() => closeSession(selectedSession._id)}
            >
              <i className="fas fa-lock me-2"></i>
              Cerrar Sesión
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Events Modal */}
      <Modal isOpen={eventsModal} toggle={closeEventsModal} size="xl">
        <ModalHeader toggle={closeEventsModal} className="bg-dark text-white">
          <i className="fas fa-list me-2"></i>
          Eventos de la Sesión
        </ModalHeader>
        <ModalBody className="p-4 bg-dark text-light">
          <div className="events-container">
            {selectedEvents && selectedEvents.length > 0 ? (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0 text-light">
                    <i className="fas fa-calendar-alt me-2"></i>
                    Historial de Eventos ({selectedEvents.length})
                  </h5>
                  <Badge color="secondary" className="fs-6">
                    Total: {selectedEvents.length}
                  </Badge>
                </div>

                <div className="events-list">
                  {selectedEvents.map((event, index) => {
                    const isCollapsed = collapsedEvents.has(index);
                    return (
                      <Card
                        key={index}
                        className="mb-3 border-0 shadow-sm bg-secondary text-light"
                      >
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center">
                              <Button
                                color="link"
                                className="p-0 me-2 text-light"
                                onClick={() => toggleEventCollapse(index)}
                                title={
                                  isCollapsed
                                    ? "Expandir evento"
                                    : "Colapsar evento"
                                }
                              >
                                <i
                                  className={`fas fa-chevron-${
                                    isCollapsed ? "right" : "down"
                                  } me-1`}
                                ></i>
                              </Button>
                              <Badge
                                color="secondary"
                                className="me-3"
                                style={{ fontSize: "0.875rem" }}
                              >
                                Evento {index + 1}
                              </Badge>
                              {event.type && (
                                <Badge
                                  color="light"
                                  className="me-2 text-dark"
                                  style={{ fontSize: "0.75rem" }}
                                >
                                  {event.type}
                                </Badge>
                              )}
                            </div>
                            {event.timestamp && (
                              <small className="text-light">
                                {formatDate(event.timestamp)}
                              </small>
                            )}
                          </div>

                          {!isCollapsed && (
                            <div className="event-data">
                              <div className="json-display bg-dark p-3 rounded border border-secondary">
                                {(() => {
                                  try {
                                    return renderJsonValue(event);
                                  } catch (error) {
                                    console.error(
                                      "Error rendering event:",
                                      error
                                    );
                                    return (
                                      <div className="text-danger">
                                        <p>Error al renderizar el evento:</p>
                                        <pre className="bg-dark text-light p-2 rounded border border-secondary">
                                          {JSON.stringify(event, null, 2)}
                                        </pre>
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center text-light py-5">
                <i className="fas fa-list fa-3x mb-3"></i>
                <p>No hay eventos disponibles</p>
                <p className="small">
                  Esta sesión no tiene eventos registrados
                </p>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className="bg-dark text-light">
          <Button color="secondary" onClick={closeEventsModal}>
            <i className="fas fa-times me-2"></i>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default connect(select)(Sessions);